import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from "react";

const SCROLL_PRELOAD_PX = 120; // right-edge distance that triggers a load
import { formatCollectionDisplayTitle } from "../lib/format";
import { fetchKpopMembers, type KpopMember } from "../lib/kpop-mapping";
import type { Collection } from "../types";
import { CollectionPickerModal } from "./CollectionPickerModal";

const PAGE_SIZE = 10;

type ExpandedMembers = {
  collectionId: string;
  members: KpopMember[];
  loading: boolean;
  error: string | null;
};

type Props = {
  collections: Collection[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  selectedMemberTag: string | null;
  onMemberSelect: (tag: string | null) => void;
  titlePrefix?: string;
  loading?: boolean;
  allLabel?: string;
};

function memberLabel(m: KpopMember): string {
  return m.stageName || m.koreanName || m.name;
}

function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

type CollectionGroupProps = {
  collection: Collection;
  selectedId: string | null;
  selectedMemberTag: string | null;
  expanded: ExpandedMembers | null;
  titlePrefix: string;
  onSelect: (id: string | null) => void;
  onMemberSelect: (tag: string | null) => void;
  onCircleClick: (
    e: MouseEvent,
    collection: Collection,
    label: string,
    active: boolean
  ) => void;
  pinned?: boolean;
};

function CollectionGroup({
  collection: c,
  selectedId,
  selectedMemberTag,
  expanded,
  titlePrefix,
  onSelect,
  onMemberSelect,
  onCircleClick,
  pinned = false,
}: CollectionGroupProps) {
  const active = c.id === selectedId;
  const label = formatCollectionDisplayTitle(c.title, titlePrefix);
  const isExpanded = expanded?.collectionId === c.id;
  const showMembers = isExpanded && expanded != null;
  const hasNewToday = isToday(c.latestProductCreatedAt);

  return (
    <div
      className={`poca-collection-group${isExpanded ? " is-expanded" : ""}${pinned ? " poca-collection-group--pinned" : ""}`}
    >
      <div className="poca-collection-group__row">
        <button
          type="button"
          className={`poca-collection${active ? " is-active" : ""}`}
          onClick={() => onSelect(active ? null : c.id)}
          aria-pressed={active}
          aria-expanded={isExpanded}
          title={label}
        >
          <span
            className="poca-collection__circle"
            onClick={(e) => void onCircleClick(e, c, label, active)}
            role="presentation"
          >
            {c.image?.url ? (
              <img
                src={c.image.url}
                alt={c.image.altText ?? label}
                loading="lazy"
              />
            ) : (
              <span className="poca-collection__initial" aria-hidden="true">
                {label.charAt(0).toUpperCase()}
              </span>
            )}
          </span>
          {hasNewToday && (
            <span className="poca-collection__new-badge" aria-label="New today">
              N
            </span>
          )}
          <span className="poca-collection__title">{label}</span>
        </button>

        <div
          className={`poca-members-strip${showMembers ? " is-open" : ""}`}
          aria-hidden={!showMembers}
        >
          {showMembers && expanded.loading && (
            <div className="poca-members-strip__loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="poca-member poca-member--skeleton"
                  style={{ "--poca-member-i": i } as CSSProperties}
                  aria-hidden="true"
                >
                  <div className="poca-member__circle" />
                </div>
              ))}
            </div>
          )}

          {showMembers && expanded.error && !expanded.loading && (
            <p className="poca-members-strip__error" role="alert">
              {expanded.error}
            </p>
          )}

          {showMembers &&
            !expanded.loading &&
            !expanded.error &&
            expanded.members.map((m, i) => {
              const name = memberLabel(m);
              const memberActive = selectedMemberTag === name;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`poca-member${memberActive ? " is-active" : ""}`}
                  style={{ "--poca-member-i": i } as CSSProperties}
                  aria-pressed={memberActive}
                  title={`${name} (${m.koreanName})`}
                  onClick={() => onMemberSelect(memberActive ? null : name)}
                >
                  <span className="poca-member__circle">
                    <span className="poca-member__initial" aria-hidden="true">
                      {name.charAt(0).toUpperCase()}
                    </span>
                  </span>
                  <span className="poca-member__title">{name}</span>
                </button>
              );
            })}
        </div>
      </div>

      <div
        className={`poca-collection-group__divider${isExpanded ? " is-open" : ""}`}
        aria-hidden="true"
      />
    </div>
  );
}

export function CollectionPills({
  collections,
  selectedId,
  onSelect,
  selectedMemberTag,
  onMemberSelect,
  titlePrefix = "",
  loading,
  allLabel = "All",
}: Props) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState<ExpandedMembers | null>(null);
  const [showFade, setShowFade] = useState(false);
  const membersAbortRef = useRef<AbortController | null>(null);
  const membersLoadedForRef = useRef<string | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const hasMoreRef = useRef(false);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [collections]);

  useEffect(() => {
    return () => membersAbortRef.current?.abort();
  }, []);

  const loadMembersForCollection = useCallback(
    (collection: Collection) => {
      const label = formatCollectionDisplayTitle(collection.title, titlePrefix);

      membersAbortRef.current?.abort();
      const ac = new AbortController();
      membersAbortRef.current = ac;

      setExpanded({
        collectionId: collection.id,
        members: [],
        loading: true,
        error: null,
      });

      void (async () => {
        try {
          const members = await fetchKpopMembers(label, ac.signal);
          if (ac.signal.aborted) return;
          setExpanded({
            collectionId: collection.id,
            members,
            loading: false,
            error: null,
          });
        } catch (err) {
          if (ac.signal.aborted) return;
          setExpanded({
            collectionId: collection.id,
            members: [],
            loading: false,
            error:
              err instanceof Error ? err.message : "Failed to load members",
          });
        }
      })();
    },
    [titlePrefix]
  );

  useEffect(() => {
    if (!selectedId) {
      membersAbortRef.current?.abort();
      setExpanded(null);
      membersLoadedForRef.current = null;
      return;
    }

    const collection = collections.find((c) => c.id === selectedId);
    if (!collection) return;

    if (membersLoadedForRef.current === selectedId) return;

    membersLoadedForRef.current = selectedId;
    loadMembersForCollection(collection);
  }, [selectedId, collections, loadMembersForCollection]);

  // Scroll-triggered load: fires when user scrolls near the right edge
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const checkFade = () => {
      const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
      setShowFade(remaining > 20);
    };

    const onScroll = () => {
      const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
      if (hasMoreRef.current && remaining < SCROLL_PRELOAD_PX) {
        setVisibleCount((n) => n + PAGE_SIZE);
      }
      setShowFade(remaining > 20);
    };

    checkFade();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading]); // re-attach after skeleton resolves to real content

  // Auto-fill: if current batch doesn't cause overflow, keep loading
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (hasMoreRef.current && el.scrollWidth <= el.clientWidth + 10) {
        setVisibleCount((n) => n + PAGE_SIZE);
      }
      setShowFade(el.scrollWidth - el.scrollLeft - el.clientWidth > 20);
    });
  }, [visibleCount, loading]);

  // PC mouse drag-to-scroll on the horizontal group filter nav
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let didDrag = false;

    const onMouseDown = (e: globalThis.MouseEvent) => {
      isDragging = true;
      didDrag = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };

    const onMouseMove = (e: globalThis.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) didDrag = true;
      if (didDrag) {
        e.preventDefault();
        el.scrollLeft = startScrollLeft - dx;
      }
    };

    const onMouseUp = () => {
      isDragging = false;
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    };

    const onMouseLeave = () => {
      if (isDragging) {
        isDragging = false;
        el.style.cursor = 'grab';
        el.style.userSelect = '';
      }
    };

    // Swallow click on children when the gesture was a drag, not a tap
    const onClickCapture = (e: globalThis.MouseEvent) => {
      if (didDrag) {
        e.stopPropagation();
        didDrag = false;
      }
    };

    el.style.cursor = 'grab';
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('click', onClickCapture, true);
      el.style.cursor = '';
      el.style.userSelect = '';
    };
  }, [loading]); // re-attach after skeleton → real nav transition

  const collapseMembers = () => {
    membersAbortRef.current?.abort();
    setExpanded(null);
  };

  const handleAllClick = () => {
    collapseMembers();
    onMemberSelect(null);
    onSelect(null);
  };

  const handleCircleClick = (
    e: MouseEvent,
    collection: Collection,
    _label: string,
    active: boolean
  ) => {
    e.stopPropagation();
    if (active) {
      handleAllClick();
      return;
    }
    onSelect(collection.id);
  };

  if (loading) {
    return (
      <div className="poca-collections poca-collections--loading" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="poca-collection poca-collection--skeleton">
            <div className="poca-collection__circle" />
            <div className="poca-collection__title-skeleton" />
          </div>
        ))}
      </div>
    );
  }

  if (collections.length === 0) return null;

  const selectedCollection =
    selectedId != null
      ? (collections.find((c) => c.id === selectedId) ?? null)
      : null;
  const visible = collections.slice(0, visibleCount);
  const scrollableCollections = visible.filter((c) => c.id !== selectedId);
  const hasMore = visibleCount < collections.length;
  hasMoreRef.current = hasMore;

  const groupProps = {
    selectedId,
    selectedMemberTag,
    expanded,
    titlePrefix,
    onSelect,
    onMemberSelect,
    onCircleClick: handleCircleClick,
  };

  return (
    <div className="poca-collections-wrap">
      <div className="poca-collections-bar">
        <div
          className={`poca-collections__leading${selectedCollection ? " poca-collections__leading--has-selected" : ""}`}
        >
          <button
            type="button"
            className={`poca-collection${selectedId == null ? " is-active" : ""}`}
            onClick={handleAllClick}
            aria-pressed={selectedId == null}
          >
            <span className="poca-collection__circle poca-collection__circle--all">
              <span aria-hidden="true">All</span>
            </span>
            <span className="poca-collection__title">{allLabel}</span>
          </button>

          <button
            type="button"
            className="poca-collection poca-collection--picker"
            onClick={() => setPickerOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
          >
            <span className="poca-collection__circle poca-collection__circle--search">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
                className="poca-collection__search-icon"
              >
                <circle
                  cx="9"
                  cy="9"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M14 14L18 18"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="poca-collection__title">Search</span>
          </button>

          {selectedCollection && (
            <CollectionGroup
              key={selectedCollection.id}
              collection={selectedCollection}
              pinned
              {...groupProps}
            />
          )}
        </div>

        <nav
          ref={(el) => { scrollContainerRef.current = el; }}
          className="poca-collections"
          aria-label="Browse collections"
        >
          {scrollableCollections.map((c) => (
            <CollectionGroup key={c.id} collection={c} {...groupProps} />
          ))}
        </nav>

        <div
          className={`poca-collections-fade${showFade ? "" : " is-hidden"}`}
          aria-hidden="true"
        />
      </div>

      <CollectionPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        collections={collections}
        selectedId={selectedId}
        onSelect={(id) => {
          onMemberSelect(null);
          onSelect(id);
        }}
        titlePrefix={titlePrefix}
      />
    </div>
  );
}
